import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  OverlayTrigger,
  Placeholder,
  Popover,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { UserHover } from "Components";
import {
  AddCollaborator,
  CreateFolder,
  CreateForm,
  DuplicateForm,
  MoveForm,
  SaveFormTemplate,
} from ".";
import { authService } from "Service/Axios";
import { useFolderList, useFormList } from "Constant/apiHooks";
import {
  FORM_BUILDER_FORM_EDIT_URL,
  FORM_BUILDER_FORM_MULTIPLE_EDIT_URL,
} from "Constant/api";
import { paths } from "Constant/paths";
import { formatDateWithShortMonth, getFirstLetterOfName } from "Utils";

export default function FormBuilderDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeFolder, setActiveFolder] = useState({
    folderId: "",
  });
  const [showDuplicateForm, setShowDuplicateForm] = useState(false);
  const [showMoveForm, setShowMoveForm] = useState({
    isShow: false,
    type: "",
  });
  const [showAddCollaborator, setShowAddCollaborator] = useState(false);
  const [showSaveFormTemplate, setShowSaveFormTemplate] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  const {
    folderList,
    loading: folderLoading,
    fetchFolderList,
  } = useFolderList();
  const {
    formList,
    setFormList,
    fetchFormList,
    loading: formLoading,
  } = useFormList(activeFolder);

  // Toggle
  const toggleDuplicateForm = (form) => {
    if (form) {
      setSelectedForm(form);
    } else {
      setSelectedForm(null);
    }
    setShowDuplicateForm(!showDuplicateForm);
  };
  const toggleMoveForm = (form, type) => {
    if (form) {
      setSelectedForm(form);
    } else {
      setSelectedForm(null);
    }
    setShowMoveForm({
      isShow: !showMoveForm.isShow,
      type,
    });
  };
  const toggleAddCollaborator = (form) => {
    if (form) {
      setSelectedForm(form);
    } else {
      setSelectedForm(null);
    }
    setShowAddCollaborator(!showAddCollaborator);
  };
  const toggleSaveFormTemplate = () => {
    setShowSaveFormTemplate(!showSaveFormTemplate);
  };
  const toggleCreateFolder = () => {
    setShowCreateFolder(!showCreateFolder);
  };
  const toggleCreateForm = (form) => {
    if (form) {
      setSelectedForm(form);
    } else {
      setSelectedForm(null);
    }
    setShowCreateForm(!showCreateForm);
  };

  const [selectedFormIds, setSelectedFormIds] = useState([]);

  // On Change
  const handleChangeForm = (e) => {
    const { name, checked } = e.target;
    const tempUser = formList.map((user) => {
      return user.formId == name ? { ...user, isChecked: checked } : user;
    });
    const formIds = tempUser
      .filter((form) => form.isChecked === true)
      .map((form) => form.formId);
    setFormList(tempUser);
    setSelectedFormIds(formIds);
  };

  // On Click
  const handleChangeFolder = (
    folderId,
    favorite,
    archive,
    recyleBin,
    template
  ) => {
    setActiveFolder({ folderId, favorite, archive, recyleBin, template });
  };
  const handleCopyFormLink = (form) => {
    const formPath = `${window.location.origin}${paths.formInstruction}/${form.formId}`;
    navigator.clipboard.writeText(formPath);
    toast.success(t("common.messages.notification.success.copyForm"));
  };

  // Listing
  useEffect(() => {
    if (folderList && folderList.length > 0 && folderList[0].gid) {
      setActiveFolder({
        folderId: folderList[0].gid,
        favorite: undefined,
        archive: undefined,
        recyleBin: undefined,
      });
    }
  }, [folderList]);

  // On Submit
  const onUpdateForm = async (form, favorite, archive, recyleBin) => {
    try {
      const payload = {
        ...form,
        favorite,
        archive,
        recyleBin,
      };
      await authService(FORM_BUILDER_FORM_EDIT_URL, "POST", payload);
      fetchFormList();
      // toast.success(messages.notification.success.createFolder);
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      }
    }
  };

  const onMultiUpdateForm = async (isDelete, isArchive) => {
    try {
      const payload = {
        ids: selectedFormIds,
        delete: isDelete,
        archive: isArchive,
      };
      if (isDelete !== undefined) {
        payload.type = "Delete";
      }
      if (isArchive !== undefined) {
        payload.type = "Archive";
      }
      await authService(FORM_BUILDER_FORM_MULTIPLE_EDIT_URL, "POST", payload);
      fetchFormList();
      if (isDelete) {
        toast.success(
          t("common.messages.notification.success.bulkDeleteForm", {
            count: selectedFormIds.length,
          })
        );
      }
      if (isArchive) {
        toast.success(
          t("common.messages.notification.success.bulkArchiveForm", {
            count: selectedFormIds.length,
          })
        );
      }
    } catch (error) {
      if (error.response && error.response.data) {
        toast.error(error.response.data.message);
      }
    }
  };

  const {
    formBuilder: {
      customizeForm: { root, formDesign },
    },
  } = paths;

  const handleRedirectToCustomizeForm = (formId) => {
    navigate(`${root}/${formId}/${formDesign}`);
  };

  return (
    <main className="main">
      <div className="formbuilder-wrapper">
        <div className="d-flex flex-wrap">
          <div className="formbuilder-navigation-sidebar rounded-start pb-3">
            <div className="height-equal">
              <div className="p-3">
                <div className="d-grid gap-3">
                  {/* <button type="button" className="btn btn-secondary">
                    <i className="bx bx-plus-circle bx-sm me-1 align-middle"></i>
                    Create New
                  </button> */}
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="secondary"
                      className="w-full"
                      id="dropdown-basic"
                    >
                      <i className="bx bx-plus-circle bx-sm me-1 align-middle"></i>
                      {t("common.button.createNew")}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      <Dropdown.Item onClick={toggleCreateFolder}>
                        {t("common.button.createFolder")}
                      </Dropdown.Item>
                      <Dropdown.Item onClick={toggleCreateForm}>
                        {t("common.button.createForm")}
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <div className="search-div">
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="pluto-search"></i>
                      </span>
                      <input
                        type="search"
                        className="form-control"
                        placeholder={t("common.placeHolder.searchCourse")}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <nav className="formbuilder-navigation">
                <ul className="m-0 p-0 d-flex flex-column height-equal">
                  {folderLoading ? (
                    <li>
                      <a>Folder Loading...</a>
                    </li>
                  ) : (
                    folderList &&
                    folderList.length > 0 &&
                    folderList.map((folder) => (
                      <li
                        key={folder.gid}
                        className={
                          folder.gid === activeFolder.folderId ? "active" : ""
                        }
                      >
                        <button
                          onClick={() => handleChangeFolder(folder.gid)}
                          className="border-0 bg-transparent"
                        >
                          {folder.name}
                        </button>{" "}
                        <span className="badge-noread">{folder.totalForm}</span>
                      </li>
                    ))
                  )}
                  <li className="divider"></li>
                  <li className={activeFolder.favorite ? "active" : ""}>
                    <button
                      onClick={() => handleChangeFolder(undefined, true)}
                      className="border-0 bg-transparent"
                    >
                      <i className="pluto-star-solid bx-xs me-1 align-middle"></i>{" "}
                      {t("common.button.myFavorites")}
                    </button>
                  </li>
                  {/* <li>
                    <a href="#">
                      <i className="pluto-users-outline bx-xs me-1 align-middle"></i>{" "}
                      Collaboration
                    </a>
                  </li> */}
                  <li
                    className={`mt-3 form-template ${
                      activeFolder.template ? "active" : ""
                    }`}
                  >
                    <button
                      onClick={() =>
                        handleChangeFolder(
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          true
                        )
                      }
                      className="border-0 bg-transparent"
                    >
                      <i className="bx bx-layout bx-xs me-1 align-middle"></i>
                      {t("common.button.formTemplate")}
                    </button>
                  </li>
                  <li
                    className={`mt-5 ${activeFolder.archive ? "active" : ""}`}
                  >
                    <button
                      onClick={() =>
                        handleChangeFolder(undefined, undefined, true)
                      }
                      className="border-0 bg-transparent"
                    >
                      <i className="bx bx-box bx-xs me-1 align-middle"></i>{" "}
                      {t("common.button.archive")}
                    </button>
                  </li>
                  <li className={`${activeFolder.recyleBin ? "active" : ""}`}>
                    <button
                      onClick={() =>
                        handleChangeFolder(
                          undefined,
                          undefined,
                          undefined,
                          true
                        )
                      }
                      className="border-0 bg-transparent"
                    >
                      <i className="bx bx-trash bx-xs me-1 align-middle"></i>{" "}
                      {t("common.button.recycleBin")}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
          <div className="formbuilder-view p-3 rounded-end bg-white">
            <div className="d-flex flex-wrap align-items-center gy-3 mb-3">
              <div className="btn-group btn-group-sm">
                <Button
                  variant="btn btn-light"
                  onClick={() => toggleMoveForm(activeFolder, "multiple")}
                  disabled={selectedFormIds.length === 0}
                >
                  <i className="bx bx-folder bx-xs me-1"></i>
                  {t("common.button.move")}
                </Button>
                <Button
                  variant="btn btn-light"
                  onClick={toggleSaveFormTemplate}
                  disabled={selectedFormIds.length === 0}
                >
                  <i className="bx bx-layout bx-xs me-1"></i>{" "}
                  {t("common.button.saveAsTemplate")}
                </Button>
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={selectedFormIds.length === 0}
                  onClick={() =>
                    activeFolder.recyleBin
                      ? onMultiUpdateForm(false, undefined)
                      : onMultiUpdateForm(true, undefined)
                  }
                >
                  <i className="bx bx-trash bx-xs me-1"></i>{" "}
                  {activeFolder.recyleBin
                    ? t("common.button.restore")
                    : t("common.button.delete")}
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  disabled={selectedFormIds.length === 0}
                  onClick={() =>
                    activeFolder.archive
                      ? onMultiUpdateForm(undefined, false)
                      : onMultiUpdateForm(undefined, true)
                  }
                >
                  <i className="bx bx-box bx-xs me-1 align-middle"></i>{" "}
                  {activeFolder.archive
                    ? t("common.button.unarchive")
                    : t("common.button.archive")}
                </button>
              </div>
              <ul className="items-view ms-auto me-0">
                <li>
                  <button className="button">
                    <i className="pluto-filter"></i>
                  </button>
                </li>
                <li>
                  <button className="button">
                    <i className="bx bx-list-ul"></i>
                  </button>
                </li>
                <li>
                  <button className="button">
                    <i className="bx bxs-grid-alt"></i>
                  </button>
                </li>
              </ul>
            </div>

            <div className="admission-tab">
              <div className="row gy-4">
                {formLoading ? (
                  [...Array(8).keys()].map((item) => (
                    <div
                      className="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-6 col-12"
                      key={item}
                    >
                      <div className="admission-box">
                        <div className="admission-header relative">
                          <div className="admission-img rounded-top-3 overflow-hidden">
                            <Placeholder animation="glow">
                              <Placeholder className="w-100 h-100" />
                            </Placeholder>
                          </div>
                        </div>
                        <div className="admission-body p-3 rounded-bottom-3 border">
                          <Placeholder animation="glow">
                            <div className="d-flex">
                              <Placeholder className="w-75 py-2" />{" "}
                              <Placeholder className="w-25 py-2 ms-1" />{" "}
                            </div>
                            <div className="d-flex gx-2 mt-3">
                              <Placeholder className="w-50 py-2" />{" "}
                              <Placeholder className="w-50 py-2 ms-1" />
                            </div>
                          </Placeholder>
                        </div>
                      </div>
                    </div>
                  ))
                ) : formList && formList.length > 0 ? (
                  formList.map((form, index) => (
                    <div
                      className="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-6 col-12"
                      key={index.toString()}
                    >
                      <div
                        className={`admission-box admission-${
                          form.status !== "Published" ? "warning" : "success"
                        } rounded-3 border h-100 ${form.status}`}
                      >
                        <div className="admission-header relative">
                          <div className="admission-img rounded-top-3 overflow-hidden">
                            <img src={form.coverImage} alt={form.formName} />
                          </div>
                          <div className="admission-header-content rounded-top-3 d-flex justify-content-center align-items-center relative">
                            <div className="header-action-bar d-flex align-items-center ps-3">
                              <div className="check-box">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="flexCheckDefault"
                                    name={form.formId || ""}
                                    checked={form.isChecked || false}
                                    onChange={handleChangeForm}
                                  />
                                </div>
                              </div>
                              <div className="ms-auto d-flex align-items-center">
                                <button
                                  type="button"
                                  className="btn-star bg-transparent text-white border-0"
                                  onClick={() =>
                                    onUpdateForm(
                                      form,
                                      !form.favorite,
                                      form.archive,
                                      form.recyleBin
                                    )
                                  }
                                >
                                  <i
                                    className={
                                      form.favorite
                                        ? "bx bxs-star"
                                        : "bx bx-star"
                                    }
                                  ></i>
                                </button>
                                <div className="dropdown">
                                  <button
                                    className="button bg-transparent border-0 d-flex align-items-center"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="bx bx-dots-vertical-rounded bx-sm"></i>
                                  </button>
                                  <ul className="dropdown-menu dropdown-menu-end border-0 shadow-sm">
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => toggleCreateForm(form)}
                                      >
                                        {t("common.button.edit")}
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() => handleCopyFormLink(form)}
                                      >
                                        {t("common.button.copyLink")}
                                      </button>
                                    </li>
                                    {/* <li>
                                      <a className="dropdown-item" href="#">
                                        Responses
                                      </a>
                                    </li> */}
                                    <li>
                                      <hr className="dropdown-divider" />
                                    </li>
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          toggleDuplicateForm(form)
                                        }
                                      >
                                        {t("common.button.duplicate")}
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          toggleMoveForm(form, "single")
                                        }
                                      >
                                        {t("common.button.move")}
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          toggleAddCollaborator(form)
                                        }
                                      >
                                        {t("common.button.collaborate")}
                                      </button>
                                    </li>
                                    <li>
                                      <hr className="dropdown-divider" />
                                    </li>
                                    {/* <li onClick={toggleSaveFormTemplate}>
                                      <a className="dropdown-item" href="#">
                                        Save as Template
                                      </a>
                                    </li> */}
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          onUpdateForm(
                                            form,
                                            form.favorite,
                                            activeFolder.archive
                                              ? false
                                              : !form.archive,
                                            form.recyleBin
                                          )
                                        }
                                      >
                                        {activeFolder.archive
                                          ? t("common.button.unarchive")
                                          : t("common.button.archive")}
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={() =>
                                          onUpdateForm(
                                            form,
                                            form.favorite,
                                            form.archive,
                                            activeFolder.recyleBin
                                              ? false
                                              : !form.recyleBin
                                          )
                                        }
                                      >
                                        {activeFolder.recyleBin
                                          ? t("common.button.restore")
                                          : t("common.button.delete")}
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                            <button
                              className="button border-0 bg-transparent full-link-position"
                              onClick={() =>
                                handleRedirectToCustomizeForm(form.formId)
                              }
                            >
                              <h3>{form.formCoverName}</h3>
                            </button>
                          </div>
                        </div>

                        <div className="admission-body p-3">
                          <div className="d-flex align-items-center">
                            <h4>{form.formName}</h4>
                            <ul className="items-view ms-auto me-0 d-flex">
                              <li>
                                <div className="applicant-dropdown">
                                  <button
                                    className="button"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                    disabled={form.collaborators?.length === 0}
                                  >
                                    <i className="pluto-users-outline"></i>
                                  </button>
                                  <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
                                    {form.collaborators &&
                                      form.collaborators.length > 0 &&
                                      form.collaborators.map(
                                        (collaborator, collaboratorIndex) => (
                                          <li
                                            className="dropdown-item"
                                            key={collaboratorIndex}
                                          >
                                            <div className="d-flex align-items-center position-relative">
                                              <OverlayTrigger
                                                placement="right"
                                                overlay={
                                                  <Popover className="max-w-unset">
                                                    <UserHover
                                                      user={collaborator}
                                                    />
                                                  </Popover>
                                                }
                                              >
                                                <div className="d-flex align-items-center">
                                                  <div className="w-8 h-8 rounded-circle overflow-hidden">
                                                    {collaborator.photoURL ? (
                                                      <img
                                                        src={
                                                          collaborator.photoURL
                                                        }
                                                        alt={`${collaborator.firstName} ${collaborator.lastName}`}
                                                        className="w-100 h-100 object-fit-cover"
                                                      />
                                                    ) : (
                                                      getFirstLetterOfName(
                                                        collaborator
                                                      )
                                                    )}
                                                  </div>

                                                  <div className="avatar-short-dtls ms-2">
                                                    <span className="mb-0 text-xs font-medium text-nowrap text-slate-500">
                                                      {collaborator.firstName}{" "}
                                                      {collaborator.lastName}
                                                    </span>
                                                  </div>
                                                </div>
                                              </OverlayTrigger>
                                              {/* <div className="tag-user me-2">
                                                {collaborator.photoURL ? (
                                                  <img
                                                    src={
                                                      collaborator.photoURL
                                                    }
                                                    alt={`${collaborator.firstName}${collaborator.lastName}`}
                                                  />
                                                ) : (
                                                  getFirstLetterOfName(
                                                    collaborator
                                                  )
                                                )}
                                              </div>
                                              <div className="applicant-name me-2">
                                                {collaborator.firstName}{" "}
                                                {collaborator.lastName}
                                                
                                              </div> */}
                                              {/* <UserHover
                                                  user={collaborator}
                                                /> */}

                                              <button
                                                type="button"
                                                className="button border-0 bg-transparent ms-auto"
                                              >
                                                <i className="bx bx-info-circle"></i>
                                              </button>
                                            </div>
                                          </li>
                                        )
                                      )}
                                  </ul>
                                </div>
                              </li>
                              <li>
                                <button
                                  className="button"
                                  onClick={() =>
                                    handleRedirectToCustomizeForm(form.formId)
                                  }
                                >
                                  <i className="bx bx-show-alt"></i>
                                </button>
                              </li>
                            </ul>
                          </div>

                          <ul className="admission-status-list m-0 mt-4 p-0 d-flex flex-wrap">
                            <li>
                              <i className="pluto-checklist-clipboard"></i>{" "}
                              {form.responses} {t("common.button.response")}
                            </li>
                            <li>
                              <i className="pluto-clock"></i>{" "}
                              {form.creationDate &&
                                formatDateWithShortMonth(form.creationDate)}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-12">
                    <div className="text-center">
                      <h4 className="text-muted">
                        {t("common.messages.error.notFound.noDataFound")}
                      </h4>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDuplicateForm && (
        <DuplicateForm
          show={showDuplicateForm}
          selectedForm={selectedForm}
          toggle={toggleDuplicateForm}
          fetchFolderList={fetchFolderList}
        />
      )}
      {showMoveForm.isShow && (
        <MoveForm
          show={showMoveForm.isShow}
          selectedForm={selectedForm}
          selectedFormIds={selectedFormIds}
          toggle={toggleMoveForm}
          fetchFolderList={fetchFolderList}
        />
      )}
      {showAddCollaborator && (
        <AddCollaborator
          show={showAddCollaborator}
          selectedForm={selectedForm}
          toggle={toggleAddCollaborator}
          fetchFolderList={fetchFolderList}
        />
      )}
      {showSaveFormTemplate && (
        <SaveFormTemplate
          show={showSaveFormTemplate}
          toggle={toggleSaveFormTemplate}
          selectedFormIds={selectedFormIds}
          fetchFormList={fetchFormList}
        />
      )}
      {showCreateFolder && (
        <CreateFolder
          show={showCreateFolder}
          toggle={toggleCreateFolder}
          fetchFolderList={fetchFolderList}
        />
      )}
      {showCreateForm && (
        <CreateForm
          show={showCreateForm}
          selectedForm={selectedForm}
          toggle={toggleCreateForm}
          fetchFolderList={fetchFolderList}
        />
      )}
    </main>
  );
}
